import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const IMAGES = {
  springVineyard: 'https://customer-assets.emergentagent.com/job_04606eda-c805-4e3a-af14-63242db4ed24/artifacts/ilu1zagr_IMG_3888.JPEG',
  logo: 'https://customer-assets.emergentagent.com/job_04606eda-c805-4e3a-af14-63242db4ed24/artifacts/q6gaf0lc_endless%20knot%20symbol%207.png',
};

const Hero = () => {
  const [loaded, setLoaded] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // `timer` is defined inside the effect; `setLoaded` is a stable setter — run once on mount
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
      <img
        src={IMAGES.springVineyard}
        alt="Geranium Bohemicum vineyards in spring"
        className="absolute inset-0 w-full h-full object-cover parallax-img"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/65" />

      <div
        className={`relative z-10 text-center px-6 max-w-3xl mx-auto transition-all duration-1200 ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDuration: '1.2s' }}
      >
        <div
          data-testid="hero-logo"
          className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 transition-all duration-1000 delay-300 ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
          style={{ transitionDelay: loaded ? '0.3s' : '0s' }}
        >
          <img src={IMAGES.logo} alt="Geranium Bohemicum emblem" className="w-full h-full object-contain" />
        </div>

        <p
          className={`font-script text-2xl md:text-3xl text-white/85 mb-3 transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '0.5s' }}
        >
          {t.hero.tagline}
        </p>

        <h1
          data-testid="hero-title"
          className={`font-heading font-light text-white leading-none tracking-tight transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ fontSize: 'clamp(3rem, 8vw, 6.5rem)', transitionDelay: '0.65s' }}
        >
          GERANIUM<br />BOHEMICUM
        </h1>

        <p
          className={`font-body text-xs md:text-sm tracking-[0.3em] uppercase text-white/65 mt-5 mb-10 transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '0.8s' }}
        >
          {t.hero.subtitle}
        </p>

        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '1s' }}
        >
          <a
            href="#wines"
            data-testid="hero-cta-wines"
            className="font-body text-xs tracking-widest uppercase px-8 py-4 bg-white/10 border border-white/50 text-white hover:bg-white hover:text-obsidian transition-all duration-300 backdrop-blur-sm"
          >
            {t.hero.cta1}
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-contact"
            className="font-body text-xs tracking-widest uppercase px-8 py-4 bg-white text-obsidian hover:bg-cream transition-all duration-300"
          >
            {t.hero.cta2}
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 animate-bounce">
        <ChevronDown size={20} />
      </div>
    </section>
  );
};

export default Hero;
